import { z } from 'zod';

import {
  CursorPaginationMetaSchema,
  ListSortOrderSchema,
  PaginationSchema,
} from '../../pagination.js';

export const StockBalanceDerivedStatusSchema = z.enum(['ok', 'out', 'low', 'third_party']);

export type StockBalanceDerivedStatus = z.infer<typeof StockBalanceDerivedStatusSchema>;

export const StockBalanceStatusFilterSchema = z.enum([
  'all',
  'alert',
  'out',
  'low',
  'pending',
  'ok',
  'third_party',
]);

export type StockBalanceStatusFilter = z.infer<typeof StockBalanceStatusFilterSchema>;

export const ListStockBalancesParamsSchema = z.object({
  placeId: z.uuid(),
});

export const StockBalanceListSortFieldSchema = z.enum([
  'productName',
  'sku',
  'category',
  'available',
  'reserved',
  'blocked',
  'minimum',
  'status',
  'network',
]);
export type StockBalanceListSortField = z.infer<typeof StockBalanceListSortFieldSchema>;

export const ListStockBalancesQuerySchema = PaginationSchema.extend({
  q: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().min(1).max(100).optional(),
  ),
  status: StockBalanceStatusFilterSchema.optional().default('all'),
  categoryId: z.uuid().optional(),
  sort: StockBalanceListSortFieldSchema.optional(),
  order: ListSortOrderSchema.optional(),
});

export type ListStockBalancesQuery = z.infer<typeof ListStockBalancesQuerySchema>;

export const StockBalanceListItemSchema = z.object({
  placeProductId: z.uuid(),
  tenantProductId: z.uuid(),
  productName: z.string(),
  productSku: z.string().nullable(),
  categoryId: z.uuid().nullable(),
  categoryName: z.string().nullable(),
  quantityAvailable: z.string(),
  quantityReserved: z.string(),
  quantityBlocked: z.string(),
  ownQuantityAvailable: z.string(),
  minimumQuantity: z.string().nullable(),
  derivedStatus: StockBalanceDerivedStatusSchema,
  hasPendingInvoice: z.boolean(),
  isThirdPartyStock: z.boolean(),
  networkQuantityAvailable: z.string().nullable(),
});

export type StockBalanceListItem = z.infer<typeof StockBalanceListItemSchema>;

export const ListStockBalancesResponseSchema = z.object({
  items: z.array(StockBalanceListItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type ListStockBalancesResponse = z.infer<typeof ListStockBalancesResponseSchema>;

export const StockBalanceSummaryParamsSchema = z.object({
  placeId: z.uuid(),
});

export const StockBalanceSummaryResponseSchema = z.object({
  skuCount: z.number().int().nonnegative(),
  alertCount: z.number().int().nonnegative(),
  ruptureCount: z.number().int().nonnegative(),
  pendingNfCount: z.number().int().nonnegative(),
  overdueDeferredCount: z.number().int().nonnegative(),
  acceleratedPendingCount: z.number().int().nonnegative(),
  estimatedStockValueCents: z.string(),
  canQuickReceipt: z.boolean(),
  deferredConsumptionMaxDays: z.number().int().positive(),
});

export type StockBalanceSummaryResponse = z.infer<typeof StockBalanceSummaryResponseSchema>;

export const StockDetailParamsSchema = z.object({
  placeProductId: z.uuid(),
});

export const StockDetailLotSchema = z.object({
  id: z.uuid(),
  lotCode: z.string(),
  ownershipType: z.enum(['OWNED', 'THIRD_PARTY', 'VIRTUAL']),
  invoiceStatus: z.enum(['PENDING', 'CONFIRMED', 'NONE']),
  invoiceNumber: z.string().nullable(),
  supplierRoleId: z.uuid().nullable(),
  supplierDisplayName: z.string().nullable(),
  thirdPartyDisplayName: z.string().nullable(),
  placeStorageId: z.uuid(),
  placeStorageName: z.string(),
  quantityAvailable: z.string(),
  quantityReserved: z.string(),
  quantityBlocked: z.string(),
  unitCostCents: z.string().nullable(),
  currency: z.string(),
  physicalReceivedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
});

export type StockDetailLot = z.infer<typeof StockDetailLotSchema>;

export const StockDetailMovementSchema = z.object({
  id: z.uuid(),
  movementType: z.string(),
  quantity: z.string(),
  lotCode: z.string().nullable(),
  placeStorageName: z.string().nullable(),
  sourceType: z.string(),
  sourceId: z.string().nullable(),
  occurredAt: z.iso.datetime(),
  unitCostCents: z.string().nullable(),
  currency: z.string().nullable(),
  reversedByMovementId: z.uuid().nullable(),
  adjustmentReason: z.string().nullable(),
});

export type StockDetailMovement = z.infer<typeof StockDetailMovementSchema>;

export const ListStockDetailMovementsQuerySchema = PaginationSchema;

export const StockDetailResponseSchema = z.object({
  placeProductId: z.uuid(),
  placeId: z.uuid(),
  tenantProductId: z.uuid(),
  productName: z.string(),
  productSku: z.string().nullable(),
  categoryName: z.string().nullable(),
  quantityAvailable: z.string(),
  quantityReserved: z.string(),
  quantityBlocked: z.string(),
  ownQuantityAvailable: z.string(),
  minimumQuantity: z.string().nullable(),
  derivedStatus: StockBalanceDerivedStatusSchema,
  hasPendingInvoice: z.boolean(),
  isThirdPartyStock: z.boolean(),
  networkQuantityAvailable: z.string().nullable(),
  lots: z.array(StockDetailLotSchema),
  movements: z.object({
    items: z.array(StockDetailMovementSchema),
    pagination: CursorPaginationMetaSchema,
  }),
});

export type StockDetailResponse = z.infer<typeof StockDetailResponseSchema>;

export const InventoryNfPendingParamsSchema = z.object({
  placeId: z.uuid(),
});

export const InventoryNfPendingKindSchema = z.enum(['RECEIPT_NO_NF', 'DEFERRED']);

export const InventoryNfPendingItemSchema = z.object({
  kind: InventoryNfPendingKindSchema,
  id: z.uuid(),
  placeProductId: z.uuid(),
  tenantProductId: z.uuid(),
  productName: z.string(),
  productSku: z.string().nullable(),
  lotId: z.uuid().nullable(),
  lotCode: z.string().nullable(),
  quantity: z.string(),
  supplierRoleId: z.uuid().nullable(),
  supplierDisplayName: z.string().nullable(),
  purchaseOrderItemId: z.uuid().nullable(),
  serviceOrderItemId: z.uuid().nullable(),
  referenceDate: z.iso.datetime(),
  ageDays: z.number().int().nonnegative(),
  isOverdue: z.boolean(),
});

export type InventoryNfPendingItem = z.infer<typeof InventoryNfPendingItemSchema>;

export const ListInventoryNfPendingResponseSchema = z.object({
  items: z.array(InventoryNfPendingItemSchema),
  deferredConsumptionMaxDays: z.number().int().positive(),
});

export type ListInventoryNfPendingResponse = z.infer<typeof ListInventoryNfPendingResponseSchema>;
